import { useState } from 'react';
import { Alert, Button, Col, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { tppMutationError } from '../../../utils/tppApiError';
import { TPP_MODAL_CONTENT_CLASS, TppBtnSpinner, TppDateField } from './tppUiShared';
import type { TppExportSubmitPayload } from './tppDataModalTypes';
import { useTppOnModalFreshOpen } from './useTppOnModalFreshOpen';

type TppExportModalProps = {
  closeLabel: string;
  show: boolean;
  onHide: () => void;
  pending: boolean;
  error: unknown;
  onSubmit: (payload: TppExportSubmitPayload) => void;
};

/** 期間（任意）だけの Excel 出力。管理ハブから開くためカード絞り込みは出さない */
export default function TppExportModal({
  closeLabel,
  show,
  onHide,
  pending,
  error,
  onSubmit,
}: TppExportModalProps) {
  const { t } = useTranslation();
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');

  useTppOnModalFreshOpen(show, () => {
    setExportFrom('');
    setExportTo('');
  });

  const rangeInvalid = !!exportFrom && !!exportTo && exportFrom > exportTo;

  return (
    <Modal show={show} onHide={onHide} centered scrollable contentClassName={TPP_MODAL_CONTENT_CLASS}>
      <Modal.Header closeButton closeLabel={closeLabel}>
        <Modal.Title>{t('tpp.export_modal_title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted mb-3">{t('tpp.export_modal_lead')}</p>
        <Row className="g-2">
          <Col xs={12} sm={6}>
            <TppDateField
              id="tpp-export-from"
              label={t('tpp.export_date_from')}
              value={exportFrom}
              onChange={setExportFrom}
            />
          </Col>
          <Col xs={12} sm={6}>
            <TppDateField
              id="tpp-export-to"
              label={t('tpp.export_date_to')}
              value={exportTo}
              onChange={setExportTo}
            />
          </Col>
        </Row>
        {rangeInvalid ? (
          <Alert className="mt-2 mb-0 py-2" variant="warning">
            {t('tpp.export_date_range_invalid')}
          </Alert>
        ) : null}
        {error != null && (
          <Alert className="mt-3 mb-0 py-2" variant="danger">
            {tppMutationError(error, t, 'tpp.export_failed')}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          disabled={pending || rangeInvalid}
          onClick={() =>
            onSubmit({
              dateFrom: exportFrom,
              dateTo: exportTo,
            })
          }
        >
          <TppBtnSpinner show={pending} />
          {t('tpp.export_confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
